import { Icon } from '@iconify/react';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './searchWidget.scss';

export default function SearchWidget({ title }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchPosts = async () => {
      if (searchTerm.trim().length < 2) {
        setResults([]);
        setShowDropdown(false);
        return;
      }

      setIsLoading(true);
      try {
        const url = `${import.meta.env.REACT_APP_CMS_URL}/items/posts?fields=id,title,hero_image&filter[status][_eq]=published&filter[title][_contains]=${encodeURIComponent(searchTerm)}&limit=5&sort=-date_created`;
        
        const res = await fetch(url);
        if (!res.ok) throw new Error('Search failed');
        
        const data = await res.json();
        setResults(data.data || []);
        setShowDropdown(true);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchPosts();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleResultClick = () => {
    setSearchTerm('');
    setShowDropdown(false);
    setResults([]);
  };

  return (
    <div ref={searchRef} className="cs_search_widget">
      <h4 className="cs-sidebar_widget_title">{title}</h4>
      <form className="cs-sidebar_search" onSubmit={(e) => e.preventDefault()}>
        <input
          type="text"
          placeholder="Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
        />
        <button className="cs-sidebar_search_btn" type="button">
          {isLoading ? (
            <Icon icon="eos-icons:loading" />
          ) : (
            <Icon icon="material-symbols:search-rounded" />
          )}
        </button>
      </form>

      {showDropdown && results.length > 0 && (
        <div className="cs_search_dropdown">
          {results.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.id}`}
              className="cs_search_result"
              onClick={handleResultClick}
            >
              <div className="cs_search_result_image">
                {post.hero_image ? (
                  <img
                    src={`${import.meta.env.REACT_APP_CMS_URL}/assets/${post.hero_image}?width=80&height=80&fit=cover`}
                    alt={post.title}
                  />
                ) : (
                  <div className="cs_search_result_placeholder">
                    <Icon icon="material-symbols:article-outline" />
                  </div>
                )}
              </div>
              <div className="cs_search_result_content">
                <span className="cs_search_result_title">{post.title}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showDropdown && searchTerm.length >= 2 && results.length === 0 && !isLoading && (
        <div className="cs_search_dropdown">
          <div className="cs_search_no_results">
            <Icon icon="material-symbols:search-off" />
            <span>No posts found</span>
          </div>
        </div>
      )}
    </div>
  );
}
